// src/modules/components/inkind/LinkedContributions.js
import React, { useCallback, useEffect, useState } from 'react';
import { useRecordContext, useNotify, usePermissions } from 'react-admin';
import {
    Box,
    Typography,
    Divider,
    Button,
    Alert,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Add as AddIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import LinkContributionModal from './LinkContributionModal';

const token = JSON.parse(localStorage.getItem('token'));

const basePathFor = (resourceType) =>
    resourceType === 'services' ? 'admin/in-kind-services' : 'admin/in-kind-offers';

const getCounterpartProduct = (counterpart) =>
    counterpart?.subCategory?.name || counterpart?.service?.name || '-';

const LinkedContributionsTable = ({ title, rows, counterpartKey, onUnlink, canManage }) => {
    if (!rows || rows.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {title}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Owner</TableCell>
                            <TableCell>Product/Service</TableCell>
                            <TableCell>Allocated Quantity</TableCell>
                            <TableCell>Note</TableCell>
                            <TableCell>Linked At</TableCell>
                            {canManage && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((link) => {
                            const counterpart = link[counterpartKey];
                            return (
                                <TableRow key={link.id} hover>
                                    <TableCell>{counterpart?.user?.name || '-'}</TableCell>
                                    <TableCell>{getCounterpartProduct(counterpart)}</TableCell>
                                    <TableCell>{link.allocated_quantity || '-'}</TableCell>
                                    <TableCell>{link.note || '-'}</TableCell>
                                    <TableCell>
                                        {link.created_at ? new Date(link.created_at).toLocaleString() : '-'}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="center">
                                            <Tooltip title="Unlink">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => onUnlink(link)}
                                                >
                                                    <LinkOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// resourceType: 'offers' | 'services' — which react-admin resource this record belongs to.
// record: pass explicitly when rendered outside a react-admin RecordContext (e.g. inside a plain MUI dialog);
// falls back to the surrounding RecordContext (e.g. inside <Show>) when omitted.
const LinkedContributions = ({ resourceType, record: recordProp }) => {
    const contextRecord = useRecordContext();
    const record = recordProp || contextRecord;
    const notify = useNotify();
    const { permissions } = usePermissions();
    const canManage = permissions === 'admin' || permissions === 'operater';

    const [links, setLinks] = useState({ asNeed: [], asDonation: [] });
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [unlinkTarget, setUnlinkTarget] = useState(null);
    const [unlinking, setUnlinking] = useState(false);

    const basePath = basePathFor(resourceType);

    const itemType = resourceType === 'services' ? record?.service_type : record?.offer_type;
    const isInKindItem = itemType === 'need' || itemType === 'donate';

    const fetchLinks = useCallback(async () => {
        if (!record?.id || !isInKindItem) return;
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_URL}/${basePath}/${record.id}/links`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );

            if (!response.ok) throw new Error('Failed to load linked contributions');

            const data = await response.json();
            setLinks({ asNeed: data.asNeed || [], asDonation: data.asDonation || [] });
        } catch (error) {
            console.error('Error loading linked contributions:', error);
            notify(error.message || 'Failed to load linked contributions', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [basePath, record?.id, isInKindItem, notify]);

    useEffect(() => {
        fetchLinks();
    }, [fetchLinks]);

    if (!record || !isInKindItem) return null;

    const hasLinks = links.asNeed.length > 0 || links.asDonation.length > 0;

    const handleUnlink = async () => {
        if (!unlinkTarget) return;
        try {
            setUnlinking(true);
            const response = await fetch(
                `${process.env.REACT_APP_URL}/${basePath}/links/${unlinkTarget.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        Accept: 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to remove link');
            }

            notify('Link removed', { type: 'success' });
            setUnlinkTarget(null);
            fetchLinks();
        } catch (error) {
            console.error('Error unlinking contribution:', error);
            notify(error.message || 'Failed to remove link', { type: 'error' });
        } finally {
            setUnlinking(false);
        }
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Linked Contributions</Typography>
                {canManage && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setModalOpen(true)}
                    >
                        Link Contribution
                    </Button>
                )}
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                </Box>
            ) : !hasLinks ? (
                <Alert severity="info">No linked contributions yet.</Alert>
            ) : (
                <>
                    <LinkedContributionsTable
                        title="Fulfilled by these donations"
                        rows={links.asNeed}
                        counterpartKey="donation"
                        onUnlink={setUnlinkTarget}
                        canManage={canManage}
                    />
                    <LinkedContributionsTable
                        title="Fulfills these needs"
                        rows={links.asDonation}
                        counterpartKey="need"
                        onUnlink={setUnlinkTarget}
                        canManage={canManage}
                    />
                </>
            )}

            <LinkContributionModal
                open={modalOpen}
                onClose={(refreshed) => {
                    setModalOpen(false);
                    if (refreshed) fetchLinks();
                }}
                record={record}
                resourceType={resourceType}
            />

            <Dialog
                open={!!unlinkTarget}
                onClose={() => !unlinking && setUnlinkTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove Link</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to unlink this contribution? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnlinkTarget(null)} disabled={unlinking}>
                        Cancel
                    </Button>
                    <Button onClick={handleUnlink} color="error" variant="contained" disabled={unlinking}>
                        {unlinking ? 'Removing...' : 'Unlink'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LinkedContributions;
